import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { sleep } from '@/utils/helpers';

import { createUserWithEmailAndPassword } from '@firebase/auth';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { firebaseAuth } from '@/firebase';
import { HttpClient } from '@angular/common/http';
import { LocalDataServiceService } from './local-data-service.service';
import { NewJoinForms } from '@/model/new-joining-form/new_JoinForms';
import { EmployeePayRoll } from '@/model/new-joining-form/employee-payroll';
import { FormGroup } from '@angular/forms';
import { ConfigService } from '@/config.service';
import { AssetDetails } from '@/model/new-joining-form/asset-details';
import { DesignationDetails } from '@/model/designation-details';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmployeeWorkSheetDetails } from '@/model/employee-work-details/employee-worksheet-details';
import { DevelopmentWorksheetDetails } from '@/model/employee-work-details/development-worksheet-details';

const provider = new GoogleAuthProvider();

@Injectable({
  providedIn: 'root'
})
export class AppService {
  public user?: User | null = null;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private http: HttpClient,
    private localData: LocalDataServiceService,
    private configServices : ConfigService
  ) {
  }

  async registerWithEmail(email: string, password: string) {
    try {
      const result = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      this.user = result.user;
      this.router.navigate(['/']);
      return result;
    } catch (error) {
      this.toastr.error(error.message);
    }
  }

  async loginWithEmail(email: string, password: string) {
    try {
      const result = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      this.user = result.user;
      this.router.navigate(['/']);

      return result;
    } catch (error) {
      this.toastr.error(error.message);
    }
  }

  async getProfile() {
    try {
      await sleep(500);
      const user = firebaseAuth.currentUser;
      if (user) {
        this.user = user;
      } else {
        this.logout();
      }
    } catch (error) {
      this.logout();
      this.toastr.error(error.message);
    }
  }

  async logout() {
    await firebaseAuth.signOut();
    this.user = null;
    this.router.navigate(['/login']);
  }

  loginAuth(formData  : FormData){
    return this.http.post<any>(
      this.configServices.getConfig().END_POINT + "/auth/login", formData
    ) 
  }

  getJoinDetails() {
    return this.http.get<NewJoinForms[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllJoinForms",
      { headers: this.localData.getHeaders() }
    )
  }

  /* get New Join ID */

  getJoinID() {
    return this.http.get(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getnewJoinId" ,
      { headers: this.localData.getHeaders(),
         responseType: 'text' as 'json'
       }
    )
  }

  /*Save NewJoining Details */

  saveNewJoining(userForms: FormGroup) {
    return this.http.post<NewJoinForms>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/newJoinForms", userForms,
      { headers: this.localData.getHeaders() }
    )
  }

  fileUpload(formData: any) {
    return this.http.post<AssetDetails[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/fileUpload/upload", formData,
      { headers: this.localData.getHeaders() }
    )
  }

  fileGet(id: number): Observable<string> {
    const headers = this.localData.getHeaders();

    return this.http.get(`${this.configServices.getConfig().END_POINT}/api/ckTech/getNewJoinAssets/${id}`, {
      headers,
      responseType: 'blob' as 'json' // ✅ trick to satisfy TypeScript
    }).pipe(
      map((blob: Blob) => URL.createObjectURL(blob)) // convert Blob -> string URL
    );
  }



  //Get Id based new joining details get 
  getByIdNewJoiningDetails(newId: number) {
    return this.http.get<NewJoinForms>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getJoinForms/" + newId,
      { headers: this.localData.getHeaders() }
    )
  }

  //Update new joining salary details
  updateNewJoiningSalaryDetails(jsonData: any) {
    return this.http.put<any>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/updateSalaryValue", jsonData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Update new joining details
  updateNewJoiningDetails(userForm: FormData) {
    return this.http.put<NewJoinForms>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/updateJoinForms", userForm,
      { headers: this.localData.getHeaders() }
    )
  }

  // Update new joining documents
  updateFileDetails(formData : any){
    return this.http.post<AssetDetails[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/fileUpload/update", formData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Check already Attendance details
  checkAlreadyAttendanceDetails(date: string) {
    return this.http.get<any[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getCurrentMonthWages/" + date,
      { headers: this.localData.getHeaders() }
    )
  }


  //Current employee details get
  getCurrentEmployeeDetails() {
    return this.http.get<NewJoinForms[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getEmployeesByFiltered",
      { headers: this.localData.getHeaders() }
    )
  }

  //Save monthly based employee payroll details
  saveEmployeePayroll(formData: FormData) {
    return this.http.post<EmployeePayRoll>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/saveEmployeePayroll", formData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Get employee payroll details by dates
  getAllPayrollMonths() {
    return this.http.get<any>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllWagesMonth",
      { headers: this.localData.getHeaders() }
    )
  }

  //Month based employee payroll details
  getPayrollMonthDetails(monthValue: string) {
    return this.http.get<EmployeePayRoll[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getPayrollDetailsByWages/" + monthValue,
      { headers: this.localData.getHeaders() }
    )
  }

  //Admin update payroll details
  updateEmployeePayrollDetails(jsonData: any) {
    return this.http.put<EmployeePayRoll>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/updateEmployeePayroll", jsonData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Designation details get all
  getAllDesignationDetails(){
    return this.http.get<DesignationDetails[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllDesignation",
      { headers: this.localData.getHeaders() }
    )
  }

  //Asset id based image delete
  deleteAsset(assetId : Number){
   return this.http.delete(
      this.configServices.getConfig().END_POINT + "/api/ckTech/asset-details/delete/" + assetId,
      { headers: this.localData.getHeaders() }
    ) 
  }

  //Employee working sheet save
  saveWorkSheet(fromData : FormData){
    return this.http.post<EmployeeWorkSheetDetails>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/saveEmployeeDailyLog" , fromData,
      { headers: this.localData.getHeaders() }
    )
  }

   //Employee working sheet delete by id
  EmployeeWorksheetDeleteById(employeeDailyWorkLogId : number){
    return this.http.delete(
      this.configServices.getConfig().END_POINT + "/api/ckTech/dailyWorkLog/delete/" + employeeDailyWorkLogId,
      { headers : this.localData.getHeaders() }
    )
  }

  //Employee based pending ticket count get
  getPendingTicketCount(){
    return this.http.get<number>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getLastAvailableCount",
      { headers: this.localData.getHeaders() }
    ) 
  }

  //Worksheet wags month get
  getListOfWagsMonth(){
     return this.http.get<string[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllWorkLogOnlyMonth",
      { headers: this.localData.getHeaders() }
    )
  }

  //Wags month based get worksheet details
  wagsMonthBasedDetailsGet(wagsMonth : string){
    return this.http.get<EmployeeWorkSheetDetails[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getEmployeeWorkLogByMonth/" + wagsMonth,
      { headers : this.localData.getHeaders() }
    )
  }

   //Development employee working sheet save
  saveDevelopmentWorkSheet(fromData : FormData){
    return this.http.post<DevelopmentWorksheetDetails>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/saveDevEmployeeTracking" , fromData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Development employee working sheet update
  updateDevelopmentWorkSheet(fromData : FormData){
    return this.http.put<DevelopmentWorksheetDetails>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/updateDevEmployeeTracking" , fromData,
      { headers: this.localData.getHeaders() }
    )
  }

  //Development employee working sheet delete by id
  developmentWorksheetDeleteById(devEmployeeTrackId : number){
    return this.http.delete(
      this.configServices.getConfig().END_POINT + "/api/ckTech/devEmployeeWorkLog/delete/" + devEmployeeTrackId,
      { headers : this.localData.getHeaders() }
    )
  }

  //Development worksheet wags month get
  getListOfWagsMonthForDevelopment(){
     return this.http.get<string[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllDevEmployeeWorkTrackOnlyMonth",
      { headers: this.localData.getHeaders() }
    )
  }

  //Development wags month based get worksheet details
  wagsMonthBasedDetailsGetForDevelopment(wagsMonth : string){
    return this.http.get<DevelopmentWorksheetDetails[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllDevEmployeeWorkTrackByMonth/" + wagsMonth,
      { headers : this.localData.getHeaders() }
    )
  }


  //Development work all work type list
   getAllWorkingTypeList(){
    return this.http.get<any[]>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getAllWorkingTypes",
      { headers : this.localData.getHeaders() }
    )
  }

  //Data entry salary value get
  dataEntryEmployeeSalaryValue(){
    return this.http.get<any>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/getSalaryValue",
      { headers : this.localData.getHeaders() }
    )
  }

  //Development team paid status update api
  updateDevelopmentWorksheetPaymentStatus(jsonData : any){
     return this.http.put<any>(
      this.configServices.getConfig().END_POINT + "/api/ckTech/updatePaymentStatus" , jsonData,
      { headers: this.localData.getHeaders() }
    )
  }
}
